import UiUtils from "../utils/ui-utils.ts";
import { popUpNotificationContainer } from "../eat-out-app.ts";
import NotificationType from "../enums/notification-type.ts";
import LogUtils from "../utils/log-utils.ts";

export default class PopUpNotification {
  private readonly lifetimeSeconds: number = 3000;
  private readonly id: string = crypto.randomUUID();

  /**
   * Creates a pop-up notification which displays information to the user.
   * @param type The type of message: INFO or ERROR.
   * @param message The message to display.
   * @param persist If true, doesn't decay the pop-up after a few seconds.
   */
  constructor(type: NotificationType, message: string, persist: boolean = false) {
    // store this locally so that we don't have to type-narrow it over and over again
    const localPopUpNotificationContainer = popUpNotificationContainer;

    // retrieve the container in which we store pop-up notifications
    if (localPopUpNotificationContainer === null) {
      LogUtils.error("attempted to display pop-up notification, but notification container was null");
    }
    if (!(localPopUpNotificationContainer instanceof HTMLDivElement)) {
      LogUtils.error("attempted to display pop-up notification, but notification container wasn't a div");
    }

    // wipe the container, because only one pop-up notification should show at a time
    localPopUpNotificationContainer.replaceChildren();

    // create the pop-up notification div
    const popUpNotification: HTMLDivElement = document.createElement("div");

    // add CSS classes
    const cssClasses: string[] = ["pop-up-notification"];
    if (type === NotificationType.ERROR) {
      cssClasses.push("color-error");
    } else { // INFO
      cssClasses.push("color-surface");
    }
    popUpNotification.classList.add(...cssClasses);

    // add custom data attribute (necessary for ID look-up during decay logic)
    popUpNotification.setAttribute("data-id", this.id);

    // add message
    const messageParagraph: HTMLParagraphElement = UiUtils.paragraph(message);
    popUpNotification.appendChild(messageParagraph);

    // push notification to container
    localPopUpNotificationContainer.appendChild(popUpNotification);

    // delete the pop-up notification after 2 seconds
    if (!persist) {
      setTimeout(() => {
        // find the pop-up div that spawned `setTimeout()`
        localPopUpNotificationContainer.childNodes.forEach((childNode) => {
          // the pop-up is always a div
          if (!(childNode instanceof HTMLDivElement)) {
            LogUtils.error("attempted to decay pop-up notification, but the child node wasn't a div");
          }

          // only remove the pop-up that matches the ID of the one that spawned `setTimeout()`
          const id: string | null = childNode.getAttribute("data-id");
          if (id === null) {
            LogUtils.error("pop-up notification was missing ID data attribute");
            return;
          }
          if (id !== this.id) {
            // TODO - pass in 'debug' value properly
            LogUtils.debug(
              globalThis.location.hostname === "localhost",
              "attempted to delete a notification that didn't match by ID",
            );
            return;
          }

          // remove the notification from the screen
          localPopUpNotificationContainer.removeChild(childNode);
        });
      }, this.lifetimeSeconds);
    }
  }
}
